<template>
  <div class="p2p-page narrow">
    <section class="p2p-hero">
      <div class="p2p-stack" style="align-items:center;gap:14px;max-width:660px">
        <span class="p2p-chip mono">{{ running ? 'Working on it' : paidOnly ? '30-day money-back guarantee' : 'Free sample · no card' }}</span>
        <h1 class="p2p-h1 xl">{{ running ? 'Writing your first posts' : 'Paste your product link' }}</h1>
        <p class="p2p-lead">{{ running ? 'We are reading your site and drafting three posts grounded in what it says. This takes about a minute.'
          : paidOnly ? 'We read your site, then you pick the formats and we write your posts.'
          : 'We read your site and write three ready-to-post drafts: two text posts and an image post.' }}</p>
      </div>
      <form v-if="!running" class="p2p-urlbar" @submit.prevent="begin">
        <label for="start-url" class="sr-only">Website address</label>
        <input id="start-url" v-model="url" type="text" inputmode="url" placeholder="yourproduct.com">
        <button type="submit" class="p2p-btn lg" :disabled="!url.trim() || starting">{{ starting ? 'Starting…' : 'Show me' }} <v-icon small color="#0b0b10">mdi-arrow-right</v-icon></button>
      </form>
      <div v-else class="p2p-stack" style="width:100%;max-width:560px">
        <div v-for="(stage, index) in stages" :key="stage" class="p2p-row p2p-start-stage">
          <v-icon v-if="index < stageIndex" small color="#34d399">mdi-check-circle</v-icon>
          <span v-else-if="index === stageIndex" class="p2p-dot is-accent pulse" style="margin:0 4px"></span>
          <span v-else class="p2p-dot" style="margin:0 4px"></span>
          <span :class="index <= stageIndex ? 'p2p-text' : 'p2p-faint'">{{ stage }}</span>
        </div>
      </div>
      <div v-if="error" class="p2p-alert bad" style="max-width:680px">
        <div class="p2p-stack" style="gap:8px">
          <span>{{ error }}</span>
          <router-link v-if="needsAccount" :to="{ path: '/register', query: { next: here } }" class="p2p-btn sm" style="align-self:flex-start">Create an account</router-link>
          <router-link v-else-if="paymentDue" to="/subscription" class="p2p-btn sm" style="align-self:flex-start">Fix payment method</router-link>
          <router-link v-else-if="needsPlanLink" :to="{ path: '/subscribe', query: { next: here } }" class="p2p-btn sm" style="align-self:flex-start">Choose a plan</router-link>
        </div>
      </div>
      <form v-if="error && !needsAccount && !running" class="p2p-stack p2p-describe" @submit.prevent="begin">
        <label class="p2p-label">Or describe your product in a sentence or two
          <textarea v-model="notes" class="p2p-textarea" rows="3" maxlength="4000" placeholder="What it does, who it is for, and what makes it different"></textarea>
        </label>
        <button type="submit" class="p2p-btn" style="align-self:flex-start" :disabled="notes.trim().length < 20">Write my sample from this</button>
      </form>
    </section>
  </div>
</template>

<script>
import { get, post, message, code } from '../api';
import { poller, active } from '../poll';

const STAGES = ['Reading your site', 'Collecting images from your site', 'Summarising your product', 'Writing your posts', 'Designing your image'];

export default {
  name: 'Start',
  data: () => ({ url: '', notes: '', running: false, starting: false, error: '', operation: null, loop: null, needsAccount: false,
    paymentDue: false, needsPlanLink: false, stages: STAGES }),
  computed: {
    paidOnly() { return this.$store.getters.forcePaidPlans; },
    // This page with the link kept, to come back to after signing up or choosing a plan.
    here() { return this.url.trim() ? '/start?url=' + encodeURIComponent(this.url.trim()) : '/start'; },
    stageIndex() {
      const stage = this.operation && this.operation.stage;
      const index = STAGES.indexOf(stage);
      return index < 0 ? 0 : index;
    }
  },
  mounted() {
    this.url = this.$route.query.url ? String(this.$route.query.url) : '';
    if (this.url) this.begin();
  },
  beforeDestroy() { if (this.loop) this.loop.stop(); },
  methods: {
    async begin() {
      this.error = '';
      this.needsAccount = false;
      this.paymentDue = false;
      this.needsPlanLink = false;
      this.starting = true;
      const user = this.$store.getters.currentUser;
      try {
        if (user && !user.is_anonymous) {
          const workspace = await this.$store.dispatch('publisher/loadWorkspace');
          // A failed payment is fixed in Billing; a member without a plan chooses one, and checkout
          // brings them back here with the link kept.
          if (workspace && workspace.access === 'payment_failed') {
            this.$router.replace('/subscription').catch(() => {});
            return;
          }
          if (workspace && workspace.access === 'none' && workspace.sample && workspace.sample.reason === 'paid_only') {
            this.$router.replace({ path: '/subscribe', query: { next: this.here } }).catch(() => {});
            return;
          }
          if (!(workspace && workspace.sample && workspace.sample.eligible)) {
            const notes = this.notes.trim();
            let project;
            try {
              project = await post('/projects', { url: this.url, notes });
            } catch (error) {
              if (!(error.response && error.response.status === 422 && notes.length >= 20)) throw error;
              project = await post('/projects', { url: '', notes });
            }
            await post('/projects/' + project.id + '/ingest', { url: project.primary_url || '', notes });
            this.$router.replace('/create/' + project.id + '/formats').catch(() => {});
            return;
          }
        }
        this.running = true;
        try {
          this.operation = await post('/samples', { url: this.url, notes: this.notes.trim() });
        } catch (error) {
          // An address we cannot use still leaves the description to work from.
          if (!(error.response && error.response.status === 422 && this.notes.trim().length >= 20)) throw error;
          this.operation = await post('/samples', { url: '', notes: this.notes.trim() });
        }
        this.loop = poller(() => this.check(), 2500);
        this.loop.start();
      } catch (error) {
        this.running = false;
        const reason = code(error);
        const signedIn = !!(user && !user.is_anonymous);
        // A signed-in member is never asked to create an account; they choose a plan or fix their payment.
        this.paymentDue = reason === 'payment_failed';
        this.needsPlanLink = signedIn && reason === 'plan_required';
        this.needsAccount = !signedIn && (reason === 'allowance_unavailable' || reason === 'plan_required' ||
          (error.response && error.response.status === 402));
        this.error = reason === 'plan_required' || reason === 'payment_failed' ? message(error)
          : this.needsAccount ? 'You have already used your free sample on this device. Create an account to keep going.' : message(error);
      } finally {
        this.starting = false;
      }
    },
    async check() {
      const operation = await get('/operations/' + this.operation.id);
      this.operation = operation;
      if (active(operation) || (operation.status === 'complete' && !operation.applied)) return true;
      if (operation.status === 'complete') {
        this.$router.replace('/create/' + operation.project_id + '/review?batch=' + operation.id).catch(() => {});
        return false;
      }
      this.running = false;
      this.error = operation.error || 'We could not finish your sample. Please try again.';
      return false;
    }
  }
};
</script>

<style>
.p2p-start-stage{gap:12px;padding:10px 14px;border:1px solid var(--p2p-line);border-radius:12px;background:var(--p2p-card);text-align:left}
.p2p-describe{width:100%;max-width:680px;text-align:left}
</style>
